/**
 * @fileoverview Compat wrappers for `@dhmk/zustand-lens` v5.
 *
 * `LensMeta` declares a string index signature, so concrete state types (no
 * index signature) are not assignable to `T & LensMeta` / `CheckLenses`. These
 * wrappers keep runtime behavior and hide that hole from call sites.
 */
import type { Lens, LensOpaqueType } from '@dhmk/zustand-lens'
import {
  lens as lensImpl,
  withLenses as withLensesImpl,
} from '@dhmk/zustand-lens'
import type { StateCreator, StoreMutatorIdentifier } from 'zustand/vanilla'

/**
 * Drop-in for `lens` that accepts a plain `T` return value.
 */
export function lens<T, S = unknown>(
  fn: (...args: Parameters<Lens<T, S>>) => T,
): LensOpaqueType<T, S> {
  return lensImpl(fn as Lens<T, S>)
}

/**
 * Drop-in for `withLenses` that accepts a plain `StateCreator` returning `T`.
 */
export function withLenses<
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(f: StateCreator<T, Mps, Mcs> | object): StateCreator<T, Mps, Mcs> {
  return withLensesImpl(f as never) as StateCreator<T, Mps, Mcs>
}
